import type { ComponentProps } from 'react';

import { usePageOperation } from '@mint-lib/datadog';
import { useFlags } from '@mint-lib/routing-context';

import type { RoleType } from '../../api/roles/index.js';
import RoleModal from './RoleModal.jsx';

type RoleModalProps = ComponentProps<typeof RoleModal>;
type PageOperation = ReturnType<typeof usePageOperation>;

// Ends the operation before closing the modal: closing unmounts it, and an
// unmount while the operation is open fails it as `abandoned`.
const submitWithOperation =
  <T,>(
    operation: PageOperation,
    submit: (options: T) => Promise<RoleType>,
    onClose: () => void,
    context: Record<string, unknown>,
  ) =>
  async (options: T) => {
    operation.restartIfEnded();
    try {
      const role = await submit(options);
      operation.succeed(context);
      onClose();
      return role;
    } catch (error) {
      operation.fail(context);
      throw error;
    }
  };

/**
 * The role modal without initial values, measured as `role.create`. Started by
 * "New role" in AddButton; the parent's submit handlers must not close the
 * modal, this component does once the operation has ended.
 */
export const CreateRoleModal = (props: RoleModalProps) => {
  const ff = useFlags('@mint/arm', ['mint2Permissions']);
  const operation = usePageOperation('role.create');
  // The flag switches between the legacy and the V2 endpoints.
  const context = { mint2Permissions: ff.mint2Permissions.enabled };

  return (
    <RoleModal
      {...props}
      onCreate={submitWithOperation(
        operation,
        props.onCreate,
        props.onClose,
        context,
      )}
      onUpdate={submitWithOperation(
        operation,
        props.onUpdate,
        props.onClose,
        context,
      )}
    />
  );
};

/**
 * The role modal opened on an existing role, measured as `role.update`.
 * Started by the role table's edit action; the parent's submit handlers must
 * not close the modal, this component does once the operation has ended.
 */
export const EditRoleModal = (props: RoleModalProps) => {
  const ff = useFlags('@mint/arm', ['mint2Permissions']);
  const operation = usePageOperation('role.update');
  const context = {
    mint2Permissions: ff.mint2Permissions.enabled,
    isDefaultRole: Boolean(props.initialValues?.isDefault),
  };

  return (
    <RoleModal
      {...props}
      onCreate={submitWithOperation(
        operation,
        props.onCreate,
        props.onClose,
        context,
      )}
      onUpdate={submitWithOperation(
        operation,
        props.onUpdate,
        props.onClose,
        context,
      )}
    />
  );
};
